// Story 048 — Edit-lock side-effect orchestrator.
//
// Mounted by `AnalysisFeature`. Reads `editPermission` (D-5 writer
// gate) and:
//   - if writer-eligible → POST /lock/acquire, schedule heartbeats,
//     release on unmount.
//   - if NOT writer-eligible → enables the polled GET /lock query so
//     the reader banner can show the current holder.
//
// Leader election (Spec 095 §"Leader-election traffic budget") is
// handled by `useLockBroadcastChannel`. Only the leader window posts
// heartbeats; non-leader windows that still hold the lock keep their
// `lockId` so they can take over on leader unload.
//
// FAILURE MODES:
//   - 409 on acquire → contention; we render the banner with the
//     contending holder's info.
//   - 409 on heartbeat → we lost the lock (server idle-released);
//     transition to free + clear our lockId.
//   - 5xx on any of the three → log + degraded snackbar (handled by
//     `useAuditUnavailableSnackbar`); do not crash.
//
// Cleanup uses navigator.sendBeacon when available so a navigation
// (page close / tab close) reliably triggers the release.
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { AxiosError, AxiosInstance } from "axios";
import { useApi } from "@aeriosiq/api-client";
import { useAuditLogAppendMutation } from "../../audit/index.js";
import {
  useAcquireLockMutation,
  useHeartbeatLockMutation,
  useLockStatusQuery,
  useReleaseLockMutation,
  type LockContentionResponse,
} from "./queries.js";
import { useLockStore } from "./lockStore.js";
import { useLockBroadcastChannel } from "./useLockBroadcastChannel.js";
import type { DefectEditPermission } from "../editPermission.js";

interface UseLockEffectArgs {
  dataAcquisitionId: string;
  /** From the D-5 writer-eligibility predicate. */
  editPermission: DefectEditPermission;
  /** Current evaluation phase from the DA detail (writer-only). */
  phase: "PRE_SCREENING" | "EXPERT_ANALYSIS" | null;
}

/**
 * DEFCAT-R2-02 — control surface returned by `useLockEffect` so the
 * contention dialog (LockMount) can drive an audited takeover or an
 * explicit self-release without reaching into the lock internals.
 */
export interface LockEffectControls {
  /**
   * Re-issue acquire with `takeover: { reason }`. Resolves on a
   * successful 200 (the holder is displaced; we transition to holding)
   * and rejects on 400/409/etc. so the dialog can surface the message.
   */
  takeover: (reason: string) => Promise<void>;
  /**
   * Explicit self-release of the lock we currently hold (idempotent —
   * a no-op when we hold nothing). Clears local lock state.
   */
  release: () => void;
  /** The lockId we currently hold, or null. */
  heldLockId: () => string | null;
}

function newWindowId(): string {
  // Deterministic ordering for leader election. We add a lex-sortable
  // prefix so newer windows tie-break later than older ones (the
  // election picks the LOWEST windowId — i.e. the oldest still-alive
  // window).
  return (
    Date.now().toString(36).padStart(8, "0") +
    "-" +
    Math.random().toString(36).slice(2, 10)
  );
}

function extractContention(err: unknown): LockContentionResponse | null {
  if (!err || typeof err !== "object") return null;
  // AeroApiError wraps the original axios error in `.cause`. We need the
  // body to surface heldBy/heldSince.
  const cause = (err as { cause?: AxiosError }).cause;
  if (!cause || typeof cause !== "object") return null;
  const data = cause.response?.data as
    | Partial<LockContentionResponse>
    | undefined;
  if (data?.code !== "lock_contention") return null;
  if (!data.heldBy || !data.heldSince) return null;
  return {
    code: "lock_contention",
    heldBy: data.heldBy,
    heldSince: data.heldSince,
    idleSince: data.idleSince ?? data.heldSince,
  };
}

function isAcquireForbidden(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const status = (err as { status?: number }).status;
  return status === 403;
}

// Fire-and-forget release. `unloading=true` uses sendBeacon (survives a
// page-close that's already torn down our fetch stack); `unloading=false`
// goes through axios so test runners' route mocking can intercept.
function fireRelease(args: {
  api: AxiosInstance;
  dataAcquisitionId: string;
  lockId: string;
  unloading: boolean;
}): void {
  const releasePath = `/data-acquisitions/${args.dataAcquisitionId}/lock/release`;
  if (
    args.unloading &&
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    const baseURL = args.api.defaults.baseURL ?? "";
    const url = `${baseURL}${releasePath}`;
    const body = JSON.stringify({ lockId: args.lockId });
    try {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    } catch {
      /* fall through to axios */
    }
  }
  void args.api.post(releasePath, { lockId: args.lockId });
}

export function useLockEffect({
  dataAcquisitionId,
  editPermission,
  phase,
}: UseLockEffectArgs): LockEffectControls {
  const api = useApi();
  const acquireMutation = useAcquireLockMutation(dataAcquisitionId);
  const heartbeatMutation = useHeartbeatLockMutation(dataAcquisitionId);
  const releaseMutation = useReleaseLockMutation(dataAcquisitionId);
  const auditMutation = useAuditLogAppendMutation();

  const isWriter = editPermission.allowed && phase !== null;

  // Reader poll — enabled only when we're NOT the writer.
  useLockStatusQuery({
    dataAcquisitionId,
    enabled: !isWriter,
  });

  // Stable per-mount windowId. Lives in a ref so we don't churn the
  // broadcast-channel hook's deps.
  const windowIdRef = useRef<string | null>(null);
  windowIdRef.current ??= newWindowId();
  const windowId = windowIdRef.current;

  // BroadcastChannel leader-election. We participate only when we're
  // the writer — readers have no heartbeat work, no need to elect.
  useLockBroadcastChannel({
    dataAcquisitionId,
    enabled: isWriter,
    windowId,
  });

  // Stable callbacks for the audit-log emitter so we don't re-fire when
  // unrelated parts of state churn.
  const auditMutate = useMemo(
    () => auditMutation.mutateAsync,
    [auditMutation.mutateAsync],
  );

  // Cleanup state across renders — the lockId we currently hold (or null).
  const heldLockIdRef = useRef<string | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Lock state from the store — drives leader-only heartbeats.
  const lockState = useLockStore((s) => s.state);
  const isLeader = lockState.kind === "holding" && lockState.isLeader;

  // --- ACQUIRE on mount when writer-eligible. --------------------------------
  useEffect(() => {
    if (!isWriter) {
      // Not a writer — reset our store snapshot to a benign state.
      const current = useLockStore.getState().state;
      if (current.kind === "holding" || current.kind === "acquiring") {
        useLockStore.getState().setFree(dataAcquisitionId);
      }
      return;
    }
    // Container so the post-await `if (lifecycle.cancelled)` reads as a
    // runtime check rather than dead code (otherwise TS narrows the
    // bare `let cancelled = false` to literal `false` forever).
    const lifecycle = { cancelled: false };
    // `phase` is narrowed by the isWriter guard above (isWriter is
    // `editPermission.allowed && phase !== null`). Capture in a local
    // alias so the async closure has a non-null reference.
    const phaseInner = phase;
    useLockStore.getState().setAcquiring(dataAcquisitionId);

    void (async () => {
      try {
        const result = await acquireMutation.mutateAsync({ phase: phaseInner });
        // Audit row — fire-and-forget; degrade silently on error. We log
        // BEFORE the cancelled check so the audit trail captures the
        // acquire regardless of a fast strict-mode double-mount.
        try {
          await auditMutate({
            entityType: "editLock",
            entityId: result.lockId,
            dataAcquisitionId,
            action: "lock:acquire",
            before: null,
            after: { phase, lockId: result.lockId },
          });
        } catch (e) {
          console.warn("[048] audit-log append for lock:acquire failed", e);
        }
        if (lifecycle.cancelled) {
          // We mounted, acquired, then unmounted before this resolved
          // (most likely React strict-mode double-mount in dev). Release
          // via axios so the test harness can observe it. The audit row
          // for the release will fire from the unmount cleanup below
          // (heldLockIdRef wasn't set, so the cleanup didn't fire its
          // own release/audit — we do it here).
          fireRelease({
            api,
            dataAcquisitionId,
            lockId: result.lockId,
            unloading: false,
          });
          void auditMutate({
            entityType: "editLock",
            entityId: result.lockId,
            dataAcquisitionId,
            action: "lock:release",
            before: { lockId: result.lockId },
            after: null,
          }).catch(() => undefined);
          return;
        }
        heldLockIdRef.current = result.lockId;
        useLockStore.getState().setHolding({
          dataAcquisitionId,
          lockId: result.lockId,
          holder: result.holder,
          heldSince: result.heldSince,
          windowId,
          isLeader: false, // leader-election will flip this in ~5s
        });
      } catch (err: unknown) {
        if (lifecycle.cancelled) return;
        const contention = extractContention(err);
        if (contention) {
          useLockStore.getState().setContended({
            dataAcquisitionId,
            heldBy: contention.heldBy,
            heldSince: contention.heldSince,
          });
          return;
        }
        if (isAcquireForbidden(err)) {
          // Server rejected the writer-eligibility check — we shouldn't
          // have been mounted as a writer. Fall back to free state.
          useLockStore.getState().setFree(dataAcquisitionId);
          return;
        }
        // Other failures (5xx, network) — log + free state. The reader
        // banner copy keys off this branch.
        console.warn("[048] lock acquire failed (degraded)", err);
        useLockStore.getState().setFree(dataAcquisitionId);
      }
    })();

    return () => {
      lifecycle.cancelled = true;
      const lockId = heldLockIdRef.current;
      heldLockIdRef.current = null;
      if (heartbeatTimerRef.current !== null) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (lockId) {
        // Release on unmount. Most cleanups are component unmounts (React
        // strict-mode, route change inside the SPA), so axios is the right
        // path — sendBeacon is reserved for actual page unload (handled
        // by the `beforeunload`/`pagehide` listener below).
        fireRelease({
          api,
          dataAcquisitionId,
          lockId,
          unloading: false,
        });
        // Best-effort audit row.
        void auditMutate({
          entityType: "editLock",
          entityId: lockId,
          dataAcquisitionId,
          action: "lock:release",
          before: { lockId },
          after: null,
        }).catch((e: unknown) => {
          console.warn("[048] audit-log append for lock:release failed", e);
        });
      }
      // Reset store when we leave the page entirely.
      useLockStore.getState().reset();
    };
    // We intentionally avoid `acquireMutation.mutateAsync` in deps — it's
    // stable across renders and re-running this effect on every render
    // would re-acquire forever. (Rule lives in *.tsx scope only; this is
    // a .ts file, so the disable comment is omitted by design.)
  }, [dataAcquisitionId, isWriter, phase, windowId]);

  // --- Beacon release on browser unload. -------------------------------------
  useEffect(() => {
    if (!isWriter) return;
    const handler = (): void => {
      const lockId = heldLockIdRef.current;
      if (!lockId) return;
      fireRelease({ api, dataAcquisitionId, lockId, unloading: true });
    };
    window.addEventListener("beforeunload", handler);
    window.addEventListener("pagehide", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
  }, [api, dataAcquisitionId, isWriter]);

  // --- Heartbeat — leader only. ---------------------------------------------
  useEffect(() => {
    if (!isLeader) {
      if (heartbeatTimerRef.current !== null) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      return;
    }
    const lockId = heldLockIdRef.current;
    if (!lockId) return;
    const intervalMs = 15_000;
    heartbeatTimerRef.current = setInterval(() => {
      void heartbeatMutation.mutateAsync({ lockId }).catch((err: unknown) => {
        const status = (err as { status?: number }).status;
        if (status === 409) {
          // Lost the lock — server idle-released us.
          heldLockIdRef.current = null;
          useLockStore.getState().setFree(dataAcquisitionId);
          return;
        }
        console.warn("[048] heartbeat failed (degraded)", err);
      });
    }, intervalMs);
    return () => {
      if (heartbeatTimerRef.current !== null) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };
  }, [dataAcquisitionId, isLeader]);

  // `releaseMutation` is exposed for tests / future programmatic release.
  // The unmount cleanup is still the canonical release path; `release`
  // below is the DEFCAT-R2-02 explicit-button path.
  void releaseMutation;

  // --- DEFCAT-R2-02 — audited takeover. -------------------------------------
  // Re-issue acquire with `takeover: { reason }`. On a 200 the BE has
  // displaced the previous holder (audited server-side); we transition
  // to holding exactly as the mount-acquire success branch does.
  const takeover = useCallback(
    async (reason: string): Promise<void> => {
      if (phase === null) {
        throw new Error("Cannot take over: no active writer phase.");
      }
      const result = await acquireMutation.mutateAsync({
        phase,
        takeover: { reason },
      });
      heldLockIdRef.current = result.lockId;
      useLockStore.getState().setHolding({
        dataAcquisitionId,
        lockId: result.lockId,
        holder: result.holder,
        heldSince: result.heldSince,
        windowId,
        isLeader: false, // leader-election flips this on the next tick
      });
      // Best-effort audit row for the takeover acquire (the BE writes the
      // authoritative takeover-reason entry; this mirrors the normal
      // acquire's client audit row).
      void auditMutate({
        entityType: "editLock",
        entityId: result.lockId,
        dataAcquisitionId,
        action: "lock:acquire",
        before: null,
        after: { phase, lockId: result.lockId, takeover: true },
      }).catch(() => undefined);
    },
    [acquireMutation, auditMutate, dataAcquisitionId, phase, windowId],
  );

  // --- DEFCAT-R2-02 — explicit self-release. --------------------------------
  // The held-lock owner ("Release" button) drops the lock without
  // navigating away. Idempotent: a no-op when we hold nothing.
  const release = useCallback((): void => {
    const lockId = heldLockIdRef.current;
    if (!lockId) return;
    heldLockIdRef.current = null;
    if (heartbeatTimerRef.current !== null) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    fireRelease({ api, dataAcquisitionId, lockId, unloading: false });
    void auditMutate({
      entityType: "editLock",
      entityId: lockId,
      dataAcquisitionId,
      action: "lock:release",
      before: { lockId },
      after: null,
    }).catch(() => undefined);
    useLockStore.getState().setFree(dataAcquisitionId);
  }, [api, auditMutate, dataAcquisitionId]);

  const heldLockId = useCallback(() => heldLockIdRef.current, []);

  return { takeover, release, heldLockId };
}
